import {
  ActionError,
  AttentionBlock,
  BulkBar,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  FilterSegments,
  RowActions,
  SearchInput,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  plural,
  toast,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { errorMessage } from '@/lib/errors'
import { useDebounced } from '@/lib/use-debounced'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { routes } from '@/routes'
import {
  useSetTemplateClinics,
  useTemplateClinics,
  type SetTemplateClinicsResult,
  type Template,
  type TemplateClinic,
  type TemplateClinicFilter,
} from './api'
import {
  activationIntro,
  arrivesByProcedure,
  canActivate,
  canDeactivate,
  clinicSituation,
  isRequiredCategory,
} from './clinic-activation'

/**
 * Aba Clínicas: em quais clínicas o documento vale (RN-DOC-48 a 50). Ativa e desativa, uma a uma ou
 * em lote, o que não vem de procedimento: as categorias exclusivas, os termos avulsos e os
 * documentos dos outros públicos. Para o termo que acompanha um procedimento do catálogo, mostra
 * onde ele está em uso: esse não depende de ativação (RN-DOC-43). Nada aqui toca no que já foi
 * emitido.
 */

const FILTERS: TemplateClinicFilter[] = ['active', 'inactive', 'all']

type Bulk = { action: 'activate' | 'deactivate'; rows: TemplateClinic[] }

export function TemplateClinicsTab({ template }: { template: Template }) {
  const isPublished = template.currentVersion !== null
  const isArchived = template.state === 'archived'

  // Abre onde há o que ver: nas clínicas que têm o documento; sem nenhuma, nas que podem recebê-lo.
  // Decidido uma vez, ao abrir: a primeira ativação não troca o segmento debaixo de quem está nele.
  const [initialFilter] = useState<TemplateClinicFilter>(() =>
    template.usage.clinicCount > 0 ? 'active' : 'inactive',
  )
  const stored = useStoredFilter('template-clinics', FILTERS, initialFilter)
  const filter = stored.filter
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [bulk, setBulk] = useState<Bulk | null>(null)
  const [failure, setFailure] = useState<{ title: string; error: unknown } | null>(null)
  const debouncedSearch = useDebounced(search.trim())

  const query = useTemplateClinics(isPublished ? template.id : undefined, {
    filter,
    search: debouncedSearch || undefined,
    page,
  })
  const save = useSetTemplateClinics()

  if (!isPublished) {
    return (
      <div className="max-w-[1120px]">
        <EmptyState
          title="Ainda não chega a nenhuma clínica"
          description="Um documento só pode ser ativado depois de publicado. Publique a primeira versão e volte aqui para escolher as clínicas."
          action={
            isArchived ? undefined : (
              <Button variant="outline" className="max-desk:min-h-touch" asChild>
                <Link to={routes.admin.publishTemplate(template.id)}>Publicar versão</Link>
              </Button>
            )
          }
        />
      </div>
    )
  }

  const rows = query.data?.data ?? []
  const counts = query.data?.meta.counts
  const fallback = query.data?.meta.fallback ?? null
  const isRequired = isRequiredCategory(template.category)

  const actionable = (row: TemplateClinic) =>
    !isArchived && (canActivate(row) || canDeactivate(row, template, fallback))
  const selectable = rows.filter(actionable)
  const chosen = selectable.filter((row) => selected.has(row.clinicId))
  const toActivate = chosen.filter(canActivate)
  const toDeactivate = chosen.filter((row) => canDeactivate(row, template, fallback))

  const options: FilterSegmentOption<TemplateClinicFilter>[] = [
    { value: 'active', label: 'Com o documento', count: counts?.active },
    { value: 'inactive', label: 'Sem o documento', count: counts?.inactive },
    { value: 'all', label: 'Todas', count: counts?.all },
  ]

  function resetList() {
    setPage(1)
    setSelected(new Set())
  }

  function toggle(clinicId: string) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(clinicId)) next.delete(clinicId)
      else next.add(clinicId)
      return next
    })
  }

  /** Desfaz uma ativação: devolve o documento que tinha saído, ou só desativa este. */
  function undoActivation(result: SetTemplateClinicsResult) {
    const entry = result.activated[0]
    if (!entry) return
    save.mutate(
      entry.replaced
        ? { templateId: entry.replaced.templateId, activateIn: [entry.clinicId] }
        : { templateId: template.id, deactivateIn: [entry.clinicId] },
      {
        onSuccess: () => toast(`Ativação desfeita em ${entry.clinicName}`),
        onError: (error) =>
          setFailure({ title: `Não conseguimos desfazer em ${entry.clinicName}`, error }),
      },
    )
  }

  function activate(row: TemplateClinic) {
    setFailure(null)
    save.mutate(
      { templateId: template.id, activateIn: [row.clinicId] },
      {
        onSuccess: (result) => {
          const replaced = result.activated[0]?.replaced
          toast(
            replaced
              ? `Ativado em ${row.clinicName} — no lugar de "${replaced.name}"`
              : `Ativado em ${row.clinicName} — vale nas próximas emissões`,
            result.activated.length > 0 ? { undo: () => undoActivation(result) } : {},
          )
        },
        onError: (error) =>
          setFailure({ title: `Não conseguimos ativar em ${row.clinicName}`, error }),
      },
    )
  }

  function deactivate(row: TemplateClinic) {
    setFailure(null)
    save.mutate(
      { templateId: template.id, deactivateIn: [row.clinicId] },
      {
        onSuccess: (result) => {
          const replacedBy = result.deactivated[0]?.replacedBy
          toast(
            replacedBy
              ? `Desativado em ${row.clinicName} — a clínica passou a usar "${replacedBy.name}"`
              : `Desativado em ${row.clinicName} — o que já foi emitido não muda`,
            result.deactivated.length > 0
              ? {
                  undo: () =>
                    save.mutate(
                      { templateId: template.id, activateIn: [row.clinicId] },
                      {
                        onSuccess: () => toast(`Ativado de novo em ${row.clinicName}`),
                        onError: (error) =>
                          setFailure({
                            title: `Não conseguimos desfazer em ${row.clinicName}`,
                            error,
                          }),
                      },
                    ),
                }
              : {},
          )
        },
        onError: (error) =>
          setFailure({ title: `Não conseguimos desativar em ${row.clinicName}`, error }),
      },
    )
  }

  function confirmBulk() {
    if (!bulk) return
    const ids = bulk.rows.map((row) => row.clinicId)
    const activating = bulk.action === 'activate'
    setFailure(null)
    save.mutate(
      activating
        ? { templateId: template.id, activateIn: ids }
        : { templateId: template.id, deactivateIn: ids },
      {
        onSuccess: (result) => {
          setBulk(null)
          setSelected(new Set())
          if (activating) {
            const replaced = result.activated.filter((entry) => entry.replaced).length
            toast(
              `Ativado em ${plural(result.activated.length, 'clínica')}${
                replaced > 0 ? ` — em ${replaced}, no lugar do documento que a clínica usava` : ''
              }`,
            )
          } else {
            toast(`Desativado em ${plural(result.deactivated.length, 'clínica')}`)
          }
        },
        onError: (error) => {
          setBulk(null)
          setFailure({
            title: activating
              ? 'Não conseguimos ativar nas clínicas selecionadas'
              : 'Não conseguimos desativar nas clínicas selecionadas',
            error,
          })
        },
      },
    )
  }

  const total = counts?.all ?? 0
  const showSearch = total > 20 || search !== ''
  const allSelected = selectable.length > 0 && chosen.length === selectable.length

  return (
    <div className="flex max-w-[1120px] flex-col gap-4">
      {isArchived ? (
        <AttentionBlock variant="lilac" title="Documento arquivado">
          Não aparece em nenhuma clínica enquanto estiver arquivado. As ativações abaixo voltam a
          valer quando ele for restaurado.
        </AttentionBlock>
      ) : (
        <p className="font-body text-secondary max-w-[840px] text-[13px] leading-[1.7] font-light">
          {activationIntro(template)}
        </p>
      )}

      {failure ? (
        <ActionError
          title={failure.title}
          description={`${errorMessage(failure.error)} Nenhuma clínica foi alterada.`}
          onDismiss={() => setFailure(null)}
        />
      ) : null}

      {query.isError ? (
        <LoadError
          title="Não conseguimos carregar as clínicas deste documento"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : !query.isPending && total === 0 ? (
        <EmptyState
          title="Nenhuma clínica cadastrada ainda"
          description="O documento vale por clínica. Cadastre a primeira: os documentos padrão do catálogo entram nela sozinhos, e os outros são ativados aqui."
          action={
            <Button variant="outline" className="max-desk:min-h-touch" asChild>
              <Link to={routes.admin.newClinic}>Criar clínica</Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <FilterSegments
              label="Filtrar clínicas"
              options={options}
              value={filter}
              onChange={(value) => {
                stored.setFilter(value)
                resetList()
              }}
            />
            {showSearch ? (
              <SearchInput
                className="tablet:max-w-[320px]"
                value={search}
                onChange={(value) => {
                  setSearch(value)
                  resetList()
                }}
                placeholder="Buscar por nome, cidade ou documento"
                aria-label="Buscar clínica"
              />
            ) : null}
          </div>

          <div>
            {query.isPending ? (
              <TableSkeleton rows={6} columns={4} />
            ) : rows.length === 0 ? (
              <EmptyState
                title={
                  debouncedSearch
                    ? `Nenhuma clínica para "${debouncedSearch}"`
                    : filter === 'active'
                      ? 'Nenhuma clínica com este documento'
                      : 'Todas as clínicas têm este documento'
                }
                description={
                  debouncedSearch
                    ? 'A busca cobre o nome fantasia, a razão social, a cidade e o documento da clínica, dentro do filtro escolhido.'
                    : filter === 'active'
                      ? arrivesByProcedure(template)
                        ? 'Ele chega sozinho quando uma clínica cadastrar um dos procedimentos que o levam. Para oferecê-lo antes disso, ative à mão.'
                        : 'A clínica só oferece o documento depois de ativado nela. Veja as clínicas sem o documento e ative onde ele deve valer.'
                      : 'Não há onde ativar: o documento já chega a todas as clínicas cadastradas.'
                }
                action={
                  debouncedSearch ? (
                    <Button
                      variant="outline"
                      className="max-desk:min-h-touch"
                      onClick={() => {
                        setSearch('')
                        resetList()
                      }}
                    >
                      Limpar busca
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="max-desk:min-h-touch"
                      onClick={() => {
                        stored.setFilter(filter === 'active' ? 'inactive' : 'active')
                        resetList()
                      }}
                    >
                      {filter === 'active'
                        ? 'Ver clínicas sem o documento'
                        : 'Ver clínicas com o documento'}
                    </Button>
                  )
                }
              />
            ) : (
              <>
                {chosen.length > 0 ? (
                  <BulkBar
                    summary={plural(chosen.length, 'clínica selecionada', 'clínicas selecionadas')}
                    onClear={() => setSelected(new Set())}
                    actions={
                      <>
                        {toActivate.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => setBulk({ action: 'activate', rows: toActivate })}
                          >
                            Ativar em {toActivate.length}
                          </button>
                        ) : null}
                        {toDeactivate.length > 0 ? (
                          <button
                            type="button"
                            onClick={() => setBulk({ action: 'deactivate', rows: toDeactivate })}
                          >
                            Desativar em {toDeactivate.length}
                          </button>
                        ) : null}
                        {allSelected ? null : (
                          <button
                            type="button"
                            onClick={() =>
                              setSelected(new Set(selectable.map((row) => row.clinicId)))
                            }
                          >
                            Selecionar todas desta página
                          </button>
                        )}
                      </>
                    }
                  />
                ) : null}
                <Table>
                  {/* A barra de seleção substitui o cabeçalho enquanto há clínicas marcadas. */}
                  {chosen.length > 0 ? null : (
                    <TableHeader>
                      <tr>
                        <TableHead>
                          <span className="flex items-center gap-3">
                            {selectable.length > 0 ? (
                              <Checkbox
                                className="size-[18px]"
                                aria-label="Selecionar todas as clínicas desta página"
                                checked={false}
                                onCheckedChange={() =>
                                  setSelected(new Set(selectable.map((row) => row.clinicId)))
                                }
                              />
                            ) : null}
                            Clínica
                          </span>
                        </TableHead>
                        <TableHead>Situação</TableHead>
                        <TableHead>Detalhe</TableHead>
                        <TableHead aria-label="Ações" />
                      </tr>
                    </TableHeader>
                  )}
                  <TableBody>
                    {rows.map((row) => {
                      const situation = clinicSituation(row, template, fallback)
                      const isSelectable = actionable(row)
                      const isSelected = selected.has(row.clinicId)
                      return (
                        <TableRow
                          key={row.clinicId}
                          selected={isSelected}
                          onClick={isSelectable ? () => toggle(row.clinicId) : undefined}
                        >
                          <TableCell primary>
                            <div className="flex items-center gap-3">
                              {isSelectable ? (
                                <Checkbox
                                  className="size-[18px]"
                                  aria-label={`Selecionar ${row.clinicName}`}
                                  checked={isSelected}
                                  onClick={(event) => event.stopPropagation()}
                                  onCheckedChange={() => toggle(row.clinicId)}
                                />
                              ) : null}
                              <div>
                                {row.clinicName}
                                <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5] font-light">
                                  {row.city}/{row.state}
                                  {row.legalName !== row.clinicName ? ` · ${row.legalName}` : ''}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell label="Situação">
                            <StatusTag level={situation.level}>{situation.label}</StatusTag>
                          </TableCell>
                          <TableCell label="Detalhe" muted>
                            <div>
                              {situation.detail}
                              {situation.reason && !isArchived ? (
                                <div className={situation.detail ? 'mt-1' : undefined}>
                                  {situation.reason}
                                </div>
                              ) : null}
                              {!situation.detail && (!situation.reason || isArchived)
                                ? 'nunca ativado'
                                : null}
                            </div>
                          </TableCell>
                          <TableCell>
                            {isSelectable ? (
                              <RowActions>
                                {row.isActive ? (
                                  <button
                                    type="button"
                                    disabled={save.isPending}
                                    onClick={() => deactivate(row)}
                                    aria-label={`Desativar em ${row.clinicName}`}
                                  >
                                    desativar
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    disabled={save.isPending}
                                    onClick={() => activate(row)}
                                    aria-label={`Ativar em ${row.clinicName}`}
                                  >
                                    {row.activeInCategory ? 'ativar no lugar' : 'ativar'}
                                  </button>
                                )}
                              </RowActions>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
                {query.data ? (
                  <ListFooter
                    meta={query.data.meta}
                    noun={['clínica', 'clínicas']}
                    note="em ordem de nome"
                    onPage={(next) => {
                      setPage(next)
                      setSelected(new Set())
                    }}
                  />
                ) : null}
              </>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={bulk !== null}
        onOpenChange={(open) => {
          if (!open) setBulk(null)
        }}
        tone="primary"
        title={
          bulk?.action === 'deactivate'
            ? `Desativar "${template.name}" em ${plural(bulk.rows.length, 'clínica')}?`
            : `Ativar "${template.name}" em ${plural(bulk?.rows.length ?? 0, 'clínica')}?`
        }
        effect={bulk ? bulkEffect(bulk, isRequired ? (fallback?.name ?? null) : null) : ''}
        confirmLabel={
          bulk?.action === 'deactivate'
            ? `Desativar em ${plural(bulk.rows.length, 'clínica')}`
            : `Ativar em ${plural(bulk?.rows.length ?? 0, 'clínica')}`
        }
        loading={save.isPending}
        loadingLabel={bulk?.action === 'deactivate' ? 'Desativando…' : 'Ativando…'}
        onConfirm={confirmBulk}
      />
    </div>
  )
}

/** O efeito real da ação em lote, e o que não se perde. */
function bulkEffect(bulk: Bulk, fallbackName: string | null) {
  const kept = 'O que já foi emitido ou assinado não muda.'
  if (bulk.action === 'deactivate') {
    return fallbackName
      ? `Essas clínicas passam a usar "${fallbackName}", o padrão do catálogo, nas próximas emissões. ${kept} Dá para ativar de novo a qualquer momento.`
      : `O documento sai das escolhas novas dessas clínicas. ${kept} Dá para ativar de novo a qualquer momento.`
  }
  const replacing = bulk.rows.filter((row) => row.activeInCategory).length
  if (replacing === 0) {
    return `O documento passa a ser oferecido nessas clínicas, nas próximas emissões. ${kept}`
  }
  return `${
    replacing === bulk.rows.length
      ? bulk.rows.length === 1
        ? 'Ele entra'
        : 'Em todas elas ele entra'
      : `Em ${replacing} delas ele entra`
  } no lugar do documento que a clínica usa hoje, nas próximas emissões. ${kept}`
}
